import { useCallback, useEffect, useState } from "react";
import * as Api from "../api-sdk";
import { getAccessToken } from "../utils/cognito";
import { useContextSelector, createContext } from "use-context-selector";
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket";
import { useProfile } from "./Profile";

const configuration = new Api.Configuration({
  basePath: import.meta.env.VITE_API_PATH,
});

const useStore = () => {
  const profile = useProfile();
  const [currentGroup, setCurrentGroup] = useState<Api.Group | null>(null);
  const [onlineMembers, setOnlineMembers] = useState<string[]>([]);
  const { sendJsonMessage, lastJsonMessage } = useWebSocket(import.meta.env.VITE_SOCKET_PATH, { share: true });

  useEffect(() => {
    if (!lastJsonMessage) return;
    if (lastJsonMessage["subject"] == "online") {
      setOnlineMembers((prev) => [...prev, lastJsonMessage["user"]]);
    }
    if (lastJsonMessage["subject"] == "offline") {
      setOnlineMembers((prev) => prev.filter((user) => user !== lastJsonMessage["user"]));
    }
    if (lastJsonMessage["subject"] == "refresh-group") {
      refresh();
    }
    if (lastJsonMessage["subject"] == "onlineUsers") {
      setOnlineMembers(lastJsonMessage["users"] || []);
    }
  }, [lastJsonMessage]);

  const loadGroupById = async (groupId: string) => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error("Failed to get access token");
      const groupApi = new Api.GroupsApi(configuration);
      const group = await groupApi.getGroup({ accessToken, id: groupId });
      setCurrentGroup(group);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!profile?.id || !currentGroup?.id) return;
    sendJsonMessage({
      action: "group",
      group: currentGroup.id,
      user: profile.id,
    });
  }, [currentGroup?.id]);

  const refresh = async () => {
    if (currentGroup?.id) {
      loadGroupById(currentGroup.id);
    }
  };

  return {
    currentGroup,
    onlineMembers,
    loadGroupById: useCallback(loadGroupById, []),
    refresh: useCallback(refresh, [currentGroup?.id]),
  };
};

// Context

interface ICurrentGroupContext {
  currentGroup: Api.Group | null;
  onlineMembers: string[];
  loadGroupById: (groupId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const CurrentGroupContext = createContext<ICurrentGroupContext | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

export const CurrentGroupProvider = (props: Props) => (
  <CurrentGroupContext.Provider value={useStore()}>{props.children}</CurrentGroupContext.Provider>
);

export const useCurrentGroup = () => useContextSelector(CurrentGroupContext, (s) => s?.currentGroup);
export const useOnlineMembers = () => useContextSelector(CurrentGroupContext, (s) => s?.onlineMembers);
export const useLoadGroupById = () => useContextSelector(CurrentGroupContext, (s) => s?.loadGroupById);
export const useRefreshCurrentGroup = () => useContextSelector(CurrentGroupContext, (s) => s?.refresh);
export const useCurrentGroupId = () => useContextSelector(CurrentGroupContext, (s) => s?.currentGroup?.id);
